import { toast } from 'sonner';

/**
 * Report the outcome of an optimistic write.
 *
 * TanStack DB applies a mutation to the local store immediately and rolls it
 * back if the server refuses, but nothing surfaces that refusal on its own: the
 * row simply reverts. Without awaiting the transaction, every write reports
 * success — including one RLS rejected — and the operator watches their change
 * appear and quietly vanish.
 *
 * `isPersisted.promise` is the signal. Awaiting it is what turns a silent
 * rollback into a message.
 */
interface PendingWrite {
  isPersisted: { promise: Promise<unknown> };
}

export async function persist(transaction: PendingWrite, message: string): Promise<boolean> {
  try {
    await transaction.isPersisted.promise;
    toast.success(message);

    return true;
  } catch (error) {
    toast.error(describeWriteError(error));

    return false;
  }
}

/**
 * Turn a PostgREST rejection into something an operator can act on.
 *
 * The raw messages name constraints and columns — "duplicate key value violates
 * unique constraint categories_account_id_slug_key" — which says what the
 * database noticed, not what the person did wrong.
 */
export function describeWriteError(error: unknown): string {
  const { code, message } = (error ?? {}) as { code?: string; message?: string };

  switch (code) {
    case '23505':
      return 'Something with that slug already exists here. Pick another.';
    case '23502':
      return 'A required field was left empty.';
    case '23503':
      return 'That references something which no longer exists. Reload and try again.';
    case '23514':
      return 'One of those values is outside what this field allows.';
    case '42501':
      return 'You do not have permission to make that change.';
    default:
      return message || 'Could not save that change.';
  }
}
